import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ALLOCATION_TYPES,
  BILLABLE_FLAGS,
  COUNTRIES,
  CURRENCIES,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  LEAVING_EMPLOYEE_STATUSES,
  WORK_MODES,
  fromMinor,
  schemas,
  toMinor,
  type Employee,
  type EmployeeImportResult,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useMoney } from '@/hooks/useMoney';
import { describeError } from '@/lib/errors';
import { formatDate, humanize } from '@/lib/format';
import { FileField, Modal, ToneBadge } from '@/components/ui';
import { CrudPage, type FieldDefinition } from '../settings/CrudPage';

/**
 * HR → Employees: the employee master, kept by HR and read by payroll and the
 * oversight roles. Added one by one or imported from a spreadsheet. An
 * employee is never deleted: leaving is a status and a last working day.
 */

const options = (values: readonly string[]) =>
  values.map((value) => ({ value, label: humanize(value) }));

const STATUS_TONE: Record<string, 'positive' | 'attention' | 'neutral' | 'negative'> = {
  ACTIVE: 'positive',
  NOTICE: 'attention',
  LONG_LEAVE: 'attention',
  RESIGNED: 'neutral',
  TERMINATED: 'negative',
};

const isBillable = (values: Record<string, unknown>) =>
  !!values.billableFlag && values.billableFlag !== 'NO';
const isLeaving = (values: Record<string, unknown>) =>
  (LEAVING_EMPLOYEE_STATUSES as string[]).includes(String(values.status ?? ''));

/** The form keeps a percentage and an amount in major units; the API, basis points and minor units. */
function toApiBody(body: Record<string, unknown>): Record<string, unknown> {
  const out = { ...body };
  if (typeof out.billablePct === 'number') out.billablePct = Math.round(out.billablePct * 100);
  if (typeof out.standardBillRate === 'number')
    out.standardBillRate = toMinor(out.standardBillRate);
  return out;
}

const EMPLOYEE_FIELDS: FieldDefinition[] = [
  {
    name: 'employeeCode',
    label: 'Employee code',
    required: true,
    help: 'e.g. EBS-0001. Unique across the companies.',
  },
  { name: 'hrisId', label: 'HRIS ID', help: 'The HR or payroll system’s ID, e.g. HROpal.' },
  { name: 'fullName', label: 'Full name', required: true },
  { name: 'displayName', label: 'Display name' },
  { name: 'officialEmail', label: 'Official email', type: 'email' },
  { name: 'designation', label: 'Designation' },
  {
    name: 'workLocation',
    label: 'Work location',
    help: 'e.g. Chetpet (KRM), Dubai.',
  },
  {
    name: 'countryCode',
    label: 'Country',
    type: 'select',
    required: true,
    options: COUNTRIES.map((country) => ({ value: country.code, label: country.name })),
  },
  {
    name: 'workMode',
    label: 'Work mode',
    type: 'select',
    required: true,
    options: options(WORK_MODES),
  },
  {
    name: 'employmentType',
    label: 'Employment type',
    type: 'select',
    required: true,
    options: options(EMPLOYMENT_TYPES),
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    required: true,
    options: options(EMPLOYEE_STATUSES),
  },
  { name: 'dateOfJoining', label: 'Date of joining', type: 'date', required: true },
  {
    name: 'lastWorkingDay',
    label: 'Last working day',
    type: 'date',
    visibleWhen: isLeaving,
    help: 'Not before the date of joining.',
  },
  {
    name: 'allocationType',
    label: 'Allocation',
    type: 'select',
    required: true,
    options: options(ALLOCATION_TYPES),
  },
  {
    name: 'billableFlag',
    label: 'Billable',
    type: 'select',
    required: true,
    options: options(BILLABLE_FLAGS),
  },
  {
    name: 'billablePct',
    label: 'Billable %',
    type: 'number',
    visibleWhen: isBillable,
    help: 'Between 0 and 100.',
  },
  {
    name: 'standardBillRate',
    label: 'Standard bill rate',
    type: 'number',
    visibleWhen: isBillable,
  },
  {
    name: 'billRateCurrency',
    label: 'Bill rate currency',
    type: 'select',
    visibleWhen: isBillable,
    options: CURRENCIES.map((currency) => ({ value: currency, label: currency })),
  },
  { name: 'remarks', label: 'Remarks' },
];

export function EmployeesPage() {
  const { can, companyId } = useAuth();
  const { company } = useMoney();
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);

  return (
    <>
      <CrudPage<Employee>
        title="Employees"
        subtitle="The employee master: who works for the company, how, and since when. Kept by HR."
        queryKey={`hr-employees-${status || 'all'}`}
        permissions={{
          read: 'employee:read',
          create: 'employee:create',
          update: 'employee:update',
        }}
        list={(query) => api.hr.employees({ ...query, ...(status ? { status } : {}) })}
        create={(body) => api.hr.createEmployee(toApiBody(body))}
        update={(id, body) => api.hr.updateEmployee(id, toApiBody(body))}
        schemas={{
          create: schemas.createEmployeeRequest,
          update: schemas.updateEmployeeRequest,
        }}
        toFormValues={(row) => ({
          ...row,
          dateOfJoining: row.dateOfJoining?.slice(0, 10) ?? '',
          lastWorkingDay: row.lastWorkingDay?.slice(0, 10) ?? '',
          billablePct: (row.billablePct ?? 0) / 100,
          standardBillRate:
            row.standardBillRate === undefined || row.standardBillRate === null
              ? ''
              : fromMinor(row.standardBillRate),
        })}
        headerActions={
          <>
            <label className="sr-only" htmlFor="employee-status-filter">
              Status
            </label>
            <select
              id="employee-status-filter"
              className="input w-40"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">All statuses</option>
              {options(EMPLOYEE_STATUSES).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {can('employee:create') ? (
              <button type="button" className="btn-secondary" onClick={() => setImporting(true)}>
                Import from Excel
              </button>
            ) : null}
          </>
        }
        columns={[
          {
            header: 'Employee',
            render: (row) => (
              <span>
                <span className="block font-medium">{row.fullName}</span>
                <span className="block font-mono text-xs text-ink-500">{row.employeeCode}</span>
              </span>
            ),
          },
          { header: 'Designation', render: (row) => row.designation ?? '—' },
          {
            header: 'Work location',
            render: (row) => [row.workLocation, row.countryCode].filter(Boolean).join(' · ') || '—',
          },
          { header: 'Type', render: (row) => humanize(row.employmentType) },
          {
            header: 'Joined',
            render: (row) => formatDate(row.dateOfJoining),
          },
          {
            header: 'Billable',
            render: (row) =>
              row.billableFlag === 'NO'
                ? 'No'
                : `${humanize(row.billableFlag)} · ${(row.billablePct ?? 0) / 100}%`,
          },
          {
            header: 'Status',
            render: (row) => (
              <ToneBadge tone={STATUS_TONE[row.status] ?? 'neutral'}>
                {humanize(row.status)}
              </ToneBadge>
            ),
          },
        ]}
        fields={EMPLOYEE_FIELDS}
        defaults={{
          countryCode: company.country,
          workMode: 'OFFICE',
          employmentType: 'PERMANENT',
          status: 'ACTIVE',
          allocationType: 'DEDICATED',
          billableFlag: 'NO',
        }}
        formColumns={2}
      />
      {importing && companyId ? (
        <ImportEmployeesModal companyId={companyId} onClose={() => setImporting(false)} />
      ) : null}
    </>
  );
}

/** The columns a sheet can carry, in the order of the template. */
const TEMPLATE_COLUMNS = [
  'Employee Code',
  'HRIS ID',
  'Full Name',
  'Display Name',
  'Official Email',
  'Designation',
  'Work Location',
  'Country Code',
  'Work Mode',
  'Employment Type',
  'Status',
  'Date of Joining',
  'Last Working Day',
  'Allocation Type',
  'Billable Flag',
  'Billable %',
  'Standard Bill Rate',
  'Bill Rate Currency',
  'Remarks',
];
const TEMPLATE_EXAMPLE = [
  'EBS-0001',
  'HRO-1001',
  'Arun Kumar',
  'Arun',
  'arun.kumar@example.com',
  'Software Engineer',
  'Chetpet (KRM)',
  'IN',
  'Hybrid',
  'Permanent',
  'Active',
  '01-04-2024',
  '',
  'Dedicated',
  'Yes',
  '100',
  '2500',
  'INR',
  '',
];

function downloadTemplate() {
  const quote = (value: string) => (/[",]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const csv = [TEMPLATE_COLUMNS, TEMPLATE_EXAMPLE]
    .map((row) => row.map(quote).join(','))
    .join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'employee-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/** Checks an employee list first, then imports it in one go. */
function ImportEmployeesModal({ companyId, onClose }: { companyId: string; onClose(): void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<EmployeeImportResult | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.hr.importEmployees({ companyId, file: file!, dryRun }),
    onSuccess: (result) => {
      setChecked(result);
      if (!result.dryRun) {
        void queryClient.invalidateQueries({
          predicate: (query) => String(query.queryKey[0]).startsWith('hr-employees'),
        });
      }
    },
  });
  const done = checked && !checked.dryRun;
  const ready = checked?.dryRun && !checked.errors.length;

  return (
    <Modal
      title="Import employees"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {done ? null : ready ? (
            <button
              type="button"
              className="btn-primary"
              disabled={run.isPending}
              onClick={() => run.mutate(false)}
            >
              Import {checked.created + checked.updated} employees
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={!file || run.isPending}
              onClick={() => run.mutate(true)}
            >
              Check file
            </button>
          )}
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-600">
        An .xlsx or .csv with <b>Employee Code</b>, <b>Full Name</b> and <b>Date of Joining</b>, and
        any of the other columns in the template. An employee whose code already exists is updated
        with the columns the sheet fills in.{' '}
        <button type="button" className="text-brand-700 hover:underline" onClick={downloadTemplate}>
          Download the template
        </button>
      </p>
      <FileField
        id="employee-import-file"
        file={file}
        accept=".xlsx,.csv"
        onChange={(next) => {
          setFile(next);
          setChecked(null);
        }}
        disabled={run.isPending || !!done}
      />

      {run.error ? <p className="mt-3 text-sm text-red-700">{describeError(run.error)}</p> : null}
      {checked ? (
        <div className="mt-4 space-y-2 text-sm">
          <p className={checked.errors.length ? 'notice-warning' : 'notice-info'}>
            {done
              ? `Imported: ${checked.created} added, ${checked.updated} updated.`
              : checked.errors.length
                ? `${checked.errors.length} row(s) need fixing before anything is imported.`
                : `Ready: ${checked.created} to add, ${checked.updated} to update.`}
          </p>
          {checked.errors.length ? (
            <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-red-700">
              {checked.errors.map((error) => (
                <li key={`${error.row}-${error.message}`}>
                  Row {error.row}: {error.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
